/**
 * Pricing Card Component
 * Displays individual pricing tier with features and CTA
 */

import React from 'react';
import { Check, Star } from 'lucide-react';
import type { PricingPlan } from '../../types/subscription';

interface PricingCardProps {
  plan: PricingPlan;
  onSelect?: () => void;
  isSelected?: boolean;
  showCTA?: boolean;
  ctaText?: string;
}

export default function PricingCard({
  plan,
  onSelect,
  isSelected = false,
  showCTA = true,
  ctaText
}: PricingCardProps) {

  const formatPrice = (price: number): string => {
    return price === 0 ? 'Free' : `$${price}`;
  };

  const getCtaText = (): string => {
    if (ctaText) return ctaText;
    if (plan.planId === 'free') return 'Get Started Free';
    if (plan.planId === 'enterprise') return 'Contact Sales';
    return 'Get Started';
  };

  const calculateExamplePrice = (): string => {
    if (plan.basePrice === 0) return '';

    const properties = 5;
    const additionalProps = Math.max(0, properties - plan.includedProperties);
    const total = plan.basePrice + (additionalProps * plan.additionalPropertyPrice);

    return `${properties} properties: $${total}/month`;
  };

  return (
    <div
      className={`relative rounded-xl p-6 transition-all ${plan.isPopular
          ? 'bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-500 shadow-lg scale-105'
          : 'bg-white border border-gray-200 hover:border-blue-300 hover:shadow-md'
        } ${isSelected ? 'ring-2 ring-blue-500 ring-offset-2' : ''}`}
    >
      {/* Popular Badge */}
      {plan.isPopular && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-1 rounded-full text-xs font-semibold flex items-center gap-1 shadow-md">
            <Star className="w-3 h-3 fill-current" />
            Most Popular
          </div>
        </div>
      )}

      {/* Plan Name */}
      <div className="text-center mb-4">
        <h3 className="text-2xl font-bold text-gray-900">{plan.name}</h3>
        {plan.description && (
          <p className="text-sm text-gray-600 mt-1">{plan.description}</p>
        )}
      </div>

      {/* Pricing */}
      <div className="text-center mb-6">
        <div className="flex items-baseline justify-center gap-1">
          <span className="text-4xl font-bold text-gray-900">
            {formatPrice(plan.basePrice)}
          </span>
          {plan.basePrice > 0 && (
            <span className="text-gray-600 text-sm">/month</span>
          )}
        </div>

        {plan.planId === 'free' && (
          <p className="text-sm text-gray-600 mt-2">
            Up to {plan.includedProperties} properties
          </p>
        )}

        {plan.includedProperties > 0 && plan.basePrice > 0 && (
          <p className="text-sm text-gray-600 mt-2">
            {plan.planId === 'base' ? 'Unlimited properties' : `Includes ${plan.includedProperties} properties`}
          </p>
        )}

        {plan.additionalPropertyPrice > 0 && (
          <p className="text-sm font-medium text-blue-600 mt-1">
            + ${plan.additionalPropertyPrice}/month per additional property
          </p>
        )}
      </div>

      {/* Features List */}
      <ul className="space-y-3 mb-6">
        {plan.features.map((feature, index) => (
          <li key={index} className="flex items-start gap-3">
            <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-gray-700">{feature}</span>
          </li>
        ))}
      </ul>

      {/* CTA Button */}
      {showCTA && (
        <button
          onClick={onSelect}
          className={`w-full py-3 px-4 rounded-lg font-medium transition-all ${plan.isPopular
              ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-lg'
              : 'bg-gray-100 hover:bg-gray-200 text-gray-900 border border-gray-300'
            }`}
        >
          {getCtaText()}
        </button>
      )}

      {/* Trial Info */}
      {plan.trialDays > 0 && showCTA && (
        <p className="text-xs text-center text-gray-500 mt-3">
          {plan.trialDays}-day free trial included
        </p>
      )}
    </div>
  );
}
